import { Suspense, useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import { StyleSheetManager } from 'styled-components';

import { createProcessingSelector } from 'store/loading/selectors';
import { OPEN_SUPPORT_FORM } from 'store/user/actionTypes';

import { ONGOING_EXPERIMENTS } from 'constants/eppo';

import { initDatadogRUM } from 'services/Datadog/initDatadogRUM';
import { initLogger } from 'services/Datadog/initLogger';
import { useFeatureFlagWithAQA } from 'services/Eppo';
import { initOneTrust } from 'services/OneTrust/initOneTrust';
import Sentry from 'services/Sentry/SentryInstance';

import { useSelector } from 'hooks';
import { useNetworkStatus } from 'hooks/common';

import { addQACommandsToWindow } from 'helpers/qaCommands';
import { isProd } from 'helpers/utils';
import { setParamsToWindow } from 'helpers/window';

import { PageLoader } from 'components/PageLoader';

import Notifications from './components/Notifications';
import router from './router';
import useI18next from './services/i18n';
import ThemeProvider from './theme/provider';

function App() {
    if (!isProd()) addQACommandsToWindow();

    const { isTranslationsLoaded } = useI18next();

    const { value: isEnabled, isReady } = useFeatureFlagWithAQA({
        flagKey: ONGOING_EXPERIMENTS.OneTrustBannerEnable,
        defaultValue: true,
        type: 'boolean',
    });

    useEffect(() => {
        Sentry.init();

        initLogger();
        initDatadogRUM();
    }, []);

    useEffect(() => {
        if (!isReady) return;

        if (isEnabled) {
            initOneTrust();
        }
    }, [isReady, isEnabled]);

    useNetworkStatus();

    setParamsToWindow();

    const isLoadingSupportForm = useSelector(createProcessingSelector([OPEN_SUPPORT_FORM]));

    return (
        isTranslationsLoaded && (
            <StyleSheetManager>
                <ThemeProvider>
                    <Notifications />
                    {isLoadingSupportForm ? (
                        <PageLoader />
                    ) : (
                        <Suspense fallback={<PageLoader />}>
                            <RouterProvider router={router} />
                        </Suspense>
                    )}
                </ThemeProvider>
            </StyleSheetManager>
        )
    );
}

export default App;
